import type { ManagementAction } from "../types/tradeManagementReport";

export interface ManagementActionLabelContext {
  baselineSlDistanceAtr?: number | null;
  baselineTpDistanceAtr?: number | null;
}

const DISTANCE_COMPARE_EPSILON = 0.05;

function formatAtrDistance(value: unknown): number | null {
  if (value == null) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function formatDistanceAdjustmentLabel(
  leg: "TP" | "SL",
  distanceAtr: number,
  baselineAtr?: number | null,
  tightenVerb?: string
): string {
  const tighten = tightenVerb ?? `Tighten ${leg}`;
  if (
    baselineAtr != null &&
    Number.isFinite(baselineAtr) &&
    distanceAtr - baselineAtr > DISTANCE_COMPARE_EPSILON
  ) {
    return `Widen ${leg} ~${distanceAtr} ATR`;
  }
  if (
    baselineAtr != null &&
    Number.isFinite(baselineAtr) &&
    baselineAtr - distanceAtr > DISTANCE_COMPARE_EPSILON
  ) {
    return `${tighten} ~${distanceAtr} ATR`;
  }
  if (baselineAtr != null && Number.isFinite(baselineAtr)) {
    return `Set ${leg} ~${distanceAtr} ATR`;
  }
  return `Widen ${leg} ~${distanceAtr} ATR`;
}

function labelTpDistanceOverride(
  params?: Record<string, unknown>,
  context?: ManagementActionLabelContext
): string {
  const intent = String(params?.intent ?? "").trim();
  const atr = formatAtrDistance(params?.distance_atr);
  const pct = formatAtrDistance(params?.distance_pct);

  if (intent === "giveback_capture") {
    if (atr != null) return `Take profit near ~${atr} ATR`;
    if (pct != null) return `Take profit near ~${pct}%`;
    return "Take profit earlier on favorable runs";
  }

  if (intent === "movement_tp_from_mfe") {
    if (atr != null) {
      return formatDistanceAdjustmentLabel(
        "TP",
        atr,
        context?.baselineTpDistanceAtr,
        "Tighten TP"
      );
    }
    if (pct != null) return `Set target ~${pct}%`;
    return "Set target from favorable excursion";
  }

  if (atr != null) {
    return formatDistanceAdjustmentLabel(
      "TP",
      atr,
      context?.baselineTpDistanceAtr,
      "Tighten TP"
    );
  }
  if (pct != null) return `Widen TP ~${pct}%`;
  return "Adjust take-profit distance";
}

function labelSlDistanceOverride(
  params?: Record<string, unknown>,
  context?: ManagementActionLabelContext
): string {
  const intent = String(params?.intent ?? "").trim();
  const atr = formatAtrDistance(params?.distance_atr);
  const pct = formatAtrDistance(params?.distance_pct);

  if (intent === "winner_stop_from_mae") {
    if (atr != null) return `Set stop ~${atr} ATR`;
    if (pct != null) return `Set stop ~${pct}%`;
    return "Set stop from winner adverse excursion";
  }

  if (atr != null) {
    return formatDistanceAdjustmentLabel(
      "SL",
      atr,
      context?.baselineSlDistanceAtr,
      "Tighten SL"
    );
  }
  if (pct != null) return `Widen SL ~${pct}%`;
  return "Adjust stop-loss distance";
}

const ACTION_LABELS: Record<
  string,
  (params?: Record<string, unknown>, context?: ManagementActionLabelContext) => string
> = {
  tp_distance_override: labelTpDistanceOverride,
  sl_distance_override: labelSlDistanceOverride,
  breakeven_move: (p) => {
    if (p?.trigger_rr != null) return `Move to breakeven at ${p.trigger_rr}R`;
    if (p?.trigger_mfe_atr != null) return `Move to breakeven after ${p.trigger_mfe_atr} ATR MFE`;
    return "Move stop to breakeven";
  },
  trailing_enable: () => "Enable trailing stop",
  trailing_activation: (p) => {
    if (p?.trigger_mfe_atr != null) return `Trail after ${p.trigger_mfe_atr} ATR MFE`;
    if (p?.trigger_rr != null) return `Trail after ${p.trigger_rr}R`;
    if (p?.trigger_swing_count != null) return `Trail after ${p.trigger_swing_count} swings`;
    return "Activate trailing stop";
  },
  trailing_update: (p) => {
    const mode = p?.mode;
    return mode ? `Update trail (${String(mode)})` : "Update trailing stop";
  },
  time_stop: (p) => {
    if (p?.max_bars != null) return `Time stop after ${p.max_bars} bars`;
    if (p?.max_minutes != null) return `Time stop after ${p.max_minutes} min`;
    return "Time-based exit";
  },
};

export function humanizeManagementAction(
  action: ManagementAction,
  context?: ManagementActionLabelContext
): string {
  const fn = ACTION_LABELS[String(action.type ?? "").trim()];
  if (fn) return fn(action.params, context);
  const type = String(action.type ?? "action").replace(/_/g, " ");
  return type.charAt(0).toUpperCase() + type.slice(1);
}

export function humanizeManagementActions(
  actions: ManagementAction[] | undefined,
  context?: ManagementActionLabelContext
): string[] {
  if (!Array.isArray(actions)) return [];
  return actions.map((action) => humanizeManagementAction(action, context));
}

export function formatSegmentKeyDisplay(segmentKey: string): string {
  return String(segmentKey ?? "")
    .split("|")
    .filter(Boolean)
    .map((part) =>
      part
        .split(/[-_]/g)
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    )
    .join(" · ");
}
